import React, { useState, useEffect, useMemo } from 'react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import { celsiusToFahrenheit } from '@/utils/temperature';
import { ChevronDown, ChevronUp, AlertTriangle, Shield, Activity } from 'lucide-react';
import { InfoTooltip } from '@/components/shared';
import {
  INJURY_RISK_TOOLTIP,
  INJURY_RISK_CONFIDENCE_TOOLTIP,
  INJURY_RISK_FACTORS_TOOLTIP,
  INJURY_RISK_AMPLIFIERS_TOOLTIP,
  INJURY_SIGNAL_TOOLTIPS,
  SUBJECTIVE_WELLNESS_TOOLTIP,
} from '@/constants/tooltips';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { InjuryRiskTimelinePoint } from '@/types';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InjuryRiskCardProps {
  athleteId?: number;
  isDark: boolean;
  defaultExpanded?: boolean;
}

interface SignalContribution {
  signal: string;
  raw_value: number;
  risk_score: number;
  weighted_contribution: number;
  direction: string;
  explanation: string;
}

interface InjuryRiskResponse {
  risk_score: number;
  tier: string;
  confidence: number;
  signal_contributions?: SignalContribution[];
  coach_actions: string[];
  interaction_penalties: string[];
  data_quality: {
    signals_present: number;
    signals_total: number;
    shap_coverage: number;
    subjective_available: boolean;
    confidence_note: string;
  };
  date: string;
  status?: string;
  message?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIER_CONFIG: Record<string, { label: string; color: string }> = {
  LOW: { label: 'Low Risk', color: 'text-green-500' },
  MODERATE: { label: 'Moderate', color: 'text-yellow-500' },
  ELEVATED: { label: 'Elevated', color: 'text-orange-500' },
  HIGH: { label: 'High Risk', color: 'text-red-500' },
  CRITICAL: { label: 'Critical', color: 'text-red-800' },
};

const TIER_COLORS: Record<string, string> = {
  LOW: '#22c55e',
  MODERATE: '#eab308',
  ELEVATED: '#f97316',
  HIGH: '#ef4444',
  CRITICAL: '#991b1b',
};

function getSignalRiskColor(riskScore: number): string {
  if (riskScore <= 0.20) return '#22c55e';
  if (riskScore <= 0.40) return '#eab308';
  if (riskScore <= 0.60) return '#f97316';
  if (riskScore <= 0.80) return '#ef4444';
  return '#991b1b';
}

function getRiskScoreColor(score: number): string {
  if (score <= 0.20) return '#22c55e';
  if (score <= 0.40) return '#eab308';
  if (score <= 0.60) return '#f97316';
  if (score <= 0.80) return '#ef4444';
  return '#991b1b';
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const InjuryRiskCard: React.FC<InjuryRiskCardProps> = ({ athleteId, isDark, defaultExpanded = false }) => {
  const [riskData, setRiskData] = useState<InjuryRiskResponse | null>(null);
  const [timelineData, setTimelineData] = useState<InjuryRiskTimelinePoint[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  // Fetch injury risk data
  useEffect(() => {
    const fetchInjuryRisk = async () => {
      try {
        setLoading(true);
        setError(null);

        const qs = 'days=45&include_details=true';
        const url = athleteId
          ? getApiUrl(`/api/injury-risk/${athleteId}/?${qs}`)
          : getApiUrl(`/api/injury-risk/?${qs}`);

        const result = await authenticatedFetchWithRetry<InjuryRiskResponse>(
          url, { method: 'GET' }, 'fetching injury risk data', 2
        );

        if (!result.success) throw new Error(result.error || 'Failed to fetch injury risk');

        if (result.data && ((result.data as any).status === 'no_data' || (result.data as any).status === 'insufficient_data')) {
          setRiskData(null);
          setLoading(false);
          return;
        }
        // Backend error wrapped in 200 — surface it as a real error
        if (result.data && (result.data as any).status === 'error') {
          throw new Error((result.data as any).error || 'Failed to fetch injury risk');
        }

        setRiskData(result.data ?? null);
      } catch (err) {
        console.error('Error fetching injury risk:', err);
        setError('Failed to fetch injury risk');
      } finally {
        setLoading(false);
      }
    };

    fetchInjuryRisk();
  }, [athleteId]);

  // Fetch timeline data (after risk loads, only if expanded or about to expand)
  useEffect(() => {
    if (!riskData || !athleteId) return;

    const fetchTimeline = async () => {
      try {
        const url = getApiUrl(`/api/injury-risk/${athleteId}/timeline/?days=45&days_timeline=28`);
        const result = await authenticatedFetchWithRetry<{ timeline: InjuryRiskTimelinePoint[] }>(
          url, { method: 'GET' }, 'fetching injury risk timeline', 1
        );
        if (result.success && result.data?.timeline) {
          setTimelineData(result.data.timeline);
        }
      } catch {
        // Timeline is non-critical
      }
    };

    fetchTimeline();
  }, [riskData, athleteId]);

  if (loading) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center justify-center h-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
            <p className="cgm-text-secondary text-sm">Calculating injury risk...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!riskData && !error) return null;
  if (error) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center gap-2 text-red-500 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }
  if (!riskData) return null;

  const tierConfig = TIER_CONFIG[riskData.tier] || TIER_CONFIG.LOW;
  const tierColor = TIER_COLORS[riskData.tier] || TIER_COLORS.LOW;
  const riskPercent = Math.round(riskData.risk_score * 100);

  const topContributors = (riskData.signal_contributions || [])
    .filter(c => c.direction === 'increasing_risk')
    .sort((a, b) => b.weighted_contribution - a.weighted_contribution)
    .slice(0, 7);

  return (
    <div
      className={`cgm-metric-card rounded-2xl p-6 transition-all duration-300 ease-in-out border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between mb-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <div className="flex items-center gap-1.5">
            <Shield className="w-5 h-5" style={{ color: tierColor }} />
            <h3 className="text-lg font-bold cgm-text-primary">Injury Risk</h3>
            <InfoTooltip
              title={INJURY_RISK_TOOLTIP.title}
              body={INJURY_RISK_TOOLTIP.body}
              isDark={isDark}
              width="w-80"
              iconSize={3.5}
              ariaLabel="How Injury Risk is calculated"
            />
          </div>
          <span className={`text-sm font-semibold px-2 py-0.5 rounded ${tierConfig.color}`} style={{ backgroundColor: `${tierColor}15` }}>
            {tierConfig.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Metadata + Confidence */}
          <div className="hidden sm:flex flex-col items-end gap-0.5">
            <div className="text-[11px] font-medium cgm-text-secondary flex items-center gap-0.5">
              Baseline Confidence
              <InfoTooltip
                title={INJURY_RISK_CONFIDENCE_TOOLTIP.title}
                body={INJURY_RISK_CONFIDENCE_TOOLTIP.body}
                isDark={isDark}
                width="w-72"
                iconSize={2.5}
                align="right"
                ariaLabel="How Baseline Confidence is calculated"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-16 h-1.5 rounded-full ${isDark ? 'bg-neutral-700' : 'bg-gray-200'} overflow-hidden`}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round(riskData.confidence * 100)}%`,
                    backgroundColor: riskData.confidence >= 0.7 ? '#3b82f6' : '#f59e0b',
                  }}
                />
              </div>
              <span className="text-xs font-semibold cgm-text-primary">
                {Math.round(riskData.confidence * 100)}%
              </span>
            </div>
            <div className="text-[9px] cgm-text-secondary font-mono">
              v1.0 Sigmoid+SHAP Composite
            </div>
          </div>
          <button
            className="text-gray-400 hover:text-gray-200 transition-colors p-1"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Score + Tier */}
      <div className="flex items-center gap-6 mb-4">
        <div>
          <div className="text-4xl font-bold" style={{ color: tierColor }}>{riskPercent}</div>
          <div className="text-[10px] cgm-text-secondary mt-1 flex items-center gap-1">
            <span className={tierConfig.color}>{tierConfig.label.toUpperCase()}</span>
          </div>
        </div>
      </div>

      {/* Coach action */}
      {riskData.coach_actions.length > 0 && (
        <div className={`flex items-start gap-2 p-3 rounded-xl ${isDark ? 'bg-neutral-800/50' : 'bg-gray-50'} border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
          <Activity className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: tierColor }} />
          <span className="text-sm cgm-text-primary">{riskData.coach_actions[0]}</span>
        </div>
      )}

      {/* Expanded Content */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[3000px] opacity-100 overflow-visible mt-5' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        {/* SHAP Waterfall / Risk Factor Attribution */}
        {topContributors.length > 0 && (
          <RiskFactorAttribution
            contributions={topContributors}
            riskScore={riskData.risk_score}
            isDark={isDark}
          />
        )}

        {/* Multi-Signal Timeline */}
        {timelineData && timelineData.length > 3 && (
          <MultiSignalTimeline data={timelineData} isDark={isDark} />
        )}

        {/* Additional Coach Actions */}
        {riskData.coach_actions.length > 1 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold cgm-text-secondary mb-2">Recommendations</h4>
            <ul className="space-y-1">
              {riskData.coach_actions.slice(1).map((action, i) => (
                <li key={i} className="flex items-start gap-2 text-sm cgm-text-primary">
                  <span className="cgm-text-secondary mt-0.5">-</span>
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Interaction Penalties */}
        {riskData.interaction_penalties.length > 0 && (
          <div className={`p-3 rounded-xl mb-4 ${isDark ? 'bg-amber-950/30 border-amber-700/30' : 'bg-amber-50 border-amber-200'} border`}>
            <h4 className={`text-xs font-semibold ${isDark ? 'text-amber-400' : 'text-amber-800'} mb-1 flex items-center gap-1`}>
              <AlertTriangle className="w-3 h-3" />
              Risk Amplifiers
              <InfoTooltip
                title={INJURY_RISK_AMPLIFIERS_TOOLTIP.title}
                body={INJURY_RISK_AMPLIFIERS_TOOLTIP.body}
                isDark={isDark}
                iconSize={3}
                ariaLabel="What Risk Amplifiers means"
              />
            </h4>
            <ul className="space-y-1">
              {riskData.interaction_penalties.map((penalty, i) => (
                <li key={i} className={`text-xs ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>{penalty}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Data Quality footer */}
        <div className={`flex items-center justify-between text-xs cgm-text-secondary p-2 rounded-lg ${isDark ? 'bg-neutral-800/50' : 'bg-gray-50'}`}>
          <span>{riskData.data_quality.signals_present}/{riskData.data_quality.signals_total} signals</span>
          <span>{riskData.data_quality.subjective_available ? 'Subjective data available' : 'Wearable data only'}</span>
          <span>{riskData.data_quality.confidence_note}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <p className="text-xs cgm-text-secondary text-center">
          Updated: {new Date(riskData.date).toLocaleDateString()} |
          {isExpanded ? ' Click to collapse' : ' Click to expand details'}
        </p>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// SHAP Waterfall / Risk Factor Attribution
// ---------------------------------------------------------------------------

interface RiskFactorAttributionProps {
  contributions: SignalContribution[];
  riskScore: number;
  isDark: boolean;
}

const BASE_RISK = 30; // Population base risk shown on the 0-100 scale

const RiskFactorAttribution: React.FC<RiskFactorAttributionProps> = ({ contributions, riskScore, isDark }) => {
  // Scale everything to 0-100 for the waterfall
  const finalScore = Math.round(riskScore * 100);

  // Calculate contribution points — each factor's share of the total score
  const totalContribWeight = contributions.reduce((s, c) => s + c.weighted_contribution, 0);

  return (
    <div className="mb-5">
      <h4 className="text-sm font-semibold cgm-text-primary mb-3 flex items-center gap-1.5">
        Risk Factor Attribution (SHAP)
        <InfoTooltip
          title={INJURY_RISK_FACTORS_TOOLTIP.title}
          body={INJURY_RISK_FACTORS_TOOLTIP.body}
          isDark={isDark}
          iconSize={3}
          ariaLabel="How each factor contributes"
        />
      </h4>
      <div className="text-xs cgm-text-secondary mb-3">How each factor contributes to this athlete's risk score</div>

      <div className="space-y-1.5">
        {/* Base Risk */}
        <WaterfallRow
          label="Base Risk (Population)"
          barWidth={BASE_RISK}
          barColor={isDark ? '#6b7280' : '#9ca3af'}
          valueLabel={`${BASE_RISK}`}
          isDark={isDark}
        />

        {/* Signal contributions — hide subjective signals with no data (raw_value === 0) */}
        {contributions
          .filter((contrib) => {
            const subjectiveSignals = ['soreness', 'soreness_3d_max', 'rpe', 'mental_fatigue'];
            if (subjectiveSignals.includes(contrib.signal) && (!contrib.raw_value || contrib.raw_value === 0)) {
              return false;
            }
            return true;
          })
          .map((contrib) => {
            const points = totalContribWeight > 0
              ? Math.round((contrib.weighted_contribution / totalContribWeight) * finalScore)
              : 0;
            const color = getSignalRiskColor(contrib.risk_score);
            const rawLabel = formatRawValue(contrib.signal, contrib.raw_value);
            const signalTooltip = INJURY_SIGNAL_TOOLTIPS[contrib.signal];

            return (
              <WaterfallRow
                key={contrib.signal}
                label={formatSignalName(contrib.signal)}
                barWidth={Math.max(points, 2)}
                barColor={color}
                valueLabel={`${rawLabel} +${points}`}
                isDark={isDark}
                tooltip={signalTooltip}
              />
            );
          })}

        {/* Final Score */}
        <div className="pt-1 mt-1 border-t border-gray-200/50 dark:border-neutral-700/50">
          <WaterfallRow
            label="Final Risk Score"
            barWidth={finalScore}
            barColor={getRiskScoreColor(riskScore)}
            valueLabel={`${finalScore}`}
            isDark={isDark}
            isFinal
          />
        </div>
      </div>

      {/* Scale reference */}
      <div className="flex justify-between mt-2 px-[140px]">
        <span className="text-[9px] cgm-text-secondary">0</span>
        <span className="text-[9px] cgm-text-secondary">25</span>
        <span className="text-[9px] cgm-text-secondary">50</span>
        <span className="text-[9px] cgm-text-secondary">75</span>
        <span className="text-[9px] cgm-text-secondary">100</span>
      </div>
    </div>
  );
};

interface WaterfallRowProps {
  label: string;
  barWidth: number;
  barColor: string;
  valueLabel: string;
  isDark: boolean;
  isFinal?: boolean;
  tooltip?: { title: string; body: string };
}

const WaterfallRow: React.FC<WaterfallRowProps> = ({ label, barWidth, barColor, valueLabel, isDark, isFinal, tooltip }) => {
  return (
    <div className="flex items-center gap-2">
      <div className="w-[130px] flex-shrink-0 flex items-center gap-1">
        <span className={`text-xs truncate ${isFinal ? 'font-bold cgm-text-primary' : 'cgm-text-secondary'}`}>
          {label}
        </span>
        {tooltip && (
          <InfoTooltip
            title={tooltip.title}
            body={tooltip.body}
            isDark={isDark}
            iconSize={3}
            ariaLabel={`About ${label}`}
          />
        )}
      </div>
      <div className="flex-1 flex items-center gap-2">
        <div className={`flex-1 h-6 rounded ${isDark ? 'bg-neutral-800' : 'bg-gray-100'} overflow-hidden relative`}>
          <div
            className="h-full rounded transition-all duration-500 flex items-center"
            style={{
              width: `${Math.min(barWidth, 100)}%`,
              backgroundColor: barColor,
              minWidth: '4px',
            }}
          />
        </div>
        <span
          className={`text-xs font-semibold w-[70px] text-right flex-shrink-0 ${isFinal ? '' : ''}`}
          style={{ color: barColor }}
        >
          {valueLabel}
        </span>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Multi-Signal Timeline
// ---------------------------------------------------------------------------

interface MultiSignalTimelineProps {
  data: InjuryRiskTimelinePoint[];
  isDark: boolean;
}

const MultiSignalTimeline: React.FC<MultiSignalTimelineProps> = ({ data, isDark }) => {
  const dates = useMemo(() => data.map(d => {
    const dt = new Date(d.date);
    return `${dt.getMonth() + 1}/${dt.getDate()}`;
  }), [data]);

  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const textColor = isDark ? '#9ca3af' : '#6b7280';

  const makeOptions = (title: string, suggestedMin?: number, suggestedMax?: number) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true, mode: 'index' as const, intersect: false },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: { display: false },
      },
      y: {
        display: true,
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 9 }, maxTicksLimit: 3 },
        suggestedMin,
        suggestedMax,
      },
    },
    elements: { point: { radius: 0, hoverRadius: 3 } },
    animation: false as const,
  });

  // Risk Score chart data
  const riskChartData = {
    labels: dates,
    datasets: [{
      label: 'Risk Score (0-100)',
      data: data.map(d => Math.round(d.risk_score * 100)),
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239,68,68,0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.3,
      pointBackgroundColor: data.map(d => getRiskScoreColor(d.risk_score)),
    }],
  };

  // ACWR chart data
  const acwrData = data.map(d => d.acwr_composite).filter(v => v !== null);
  const hasAcwr = acwrData.length > 3;
  const acwrChartData = {
    labels: dates,
    datasets: [
      {
        label: 'ACWR',
        data: data.map(d => d.acwr_composite),
        borderColor: '#3b82f6',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
      },
      // Safe zone bands
      {
        label: 'Safe Zone Upper',
        data: data.map(() => 1.3),
        borderColor: 'rgba(34,197,94,0.3)',
        borderWidth: 1,
        borderDash: [4, 4],
        fill: false,
        pointRadius: 0,
      },
      {
        label: 'Safe Zone Lower',
        data: data.map(() => 0.8),
        borderColor: 'rgba(34,197,94,0.3)',
        borderWidth: 1,
        borderDash: [4, 4],
        fill: '-1',
        backgroundColor: 'rgba(34,197,94,0.05)',
        pointRadius: 0,
      },
    ],
  };

  // HRV chart data
  const hrvData = data.map(d => d.hrv_rmssd).filter(v => v !== null);
  const hasHrv = hrvData.length > 3;
  const hrvBaseline = hrvData.length > 0 ? hrvData.reduce((s, v) => s + (v || 0), 0) / hrvData.length : 0;
  const hrvChartData = {
    labels: dates,
    datasets: [
      {
        label: 'HRV (RMSSD, ms)',
        data: data.map(d => d.hrv_rmssd),
        borderColor: '#8b5cf6',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
      },
      {
        label: 'Baseline',
        data: data.map(() => Math.round(hrvBaseline)),
        borderColor: 'rgba(139,92,246,0.3)',
        borderWidth: 1,
        borderDash: [6, 3],
        fill: false,
        pointRadius: 0,
      },
    ],
  };

  // Subjective Wellness (composite of 4 session logging signals)
  const subjectiveData = data.map(d => {
    const values: number[] = [];
    if (d.soreness !== null) values.push(10 - d.soreness); // invert: low soreness = good
    if (d.mental_fatigue !== null) values.push(10 - d.mental_fatigue); // invert: low fatigue = good
    if (d.rpe !== null) values.push(10 - d.rpe); // invert: low RPE = good
    if (d.motivation !== null) values.push(d.motivation); // direct: high motivation = good
    return values.length > 0 ? Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 10) / 10 : null;
  });
  const hasSubjective = subjectiveData.filter(v => v !== null).length > 3;
  const subjectiveChartData = {
    labels: dates,
    datasets: [{
      label: 'Subjective Wellness (0-10)',
      data: subjectiveData,
      borderColor: '#22c55e',
      borderWidth: 2,
      fill: false,
      tension: 0.3,
    }],
  };

  const charts = [
    { title: 'Risk Score (0-100)', data: riskChartData, options: makeOptions('Risk Score', 0, 100), show: true, color: '#ef4444', tooltip: undefined as { title: string; body: string } | undefined },
    { title: 'ACWR (Acute:Chronic Workload Ratio)', data: acwrChartData, options: makeOptions('ACWR', 0, 2), show: hasAcwr, color: '#3b82f6', tooltip: undefined },
    { title: 'HRV (RMSSD, ms)', data: hrvChartData, options: makeOptions('HRV'), show: hasHrv, color: '#8b5cf6', tooltip: undefined },
    { title: 'Subjective Wellness (0-10)', data: subjectiveChartData, options: makeOptions('Wellness', 0, 10), show: hasSubjective, color: '#22c55e', tooltip: SUBJECTIVE_WELLNESS_TOOLTIP },
  ];

  return (
    <div className="mb-5">
      <h4 className="text-sm font-semibold cgm-text-primary mb-1">Multi-Signal Timeline</h4>
      <p className="text-xs cgm-text-secondary mb-3">28-day view of aligned biometric signals</p>

      <div className="space-y-1">
        {charts.filter(c => c.show).map((chart) => (
          <div key={chart.title}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: chart.color }} />
              <span className="text-[10px] font-medium cgm-text-secondary">{chart.title}</span>
              {chart.tooltip && (
                <InfoTooltip
                  title={chart.tooltip.title}
                  body={chart.tooltip.body}
                  isDark={isDark}
                  iconSize={2.5}
                  ariaLabel={`About ${chart.title}`}
                />
              )}
            </div>
            <div style={{ height: '80px' }}>
              <Line data={chart.data} options={chart.options} />
            </div>
          </div>
        ))}

        {/* Shared x-axis labels */}
        <div className="flex justify-between px-[40px]">
          {dates.filter((_, i) => i % Math.max(1, Math.floor(dates.length / 7)) === 0 || i === dates.length - 1).map((d, i) => (
            <span key={i} className="text-[9px] cgm-text-secondary">{d}</span>
          ))}
        </div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSignalName(signal: string): string {
  const names: Record<string, string> = {
    soreness_3d_max: '3-Day Max Soreness',
    soreness: 'Soreness',
    mental_fatigue: 'Mental Fatigue',
    rpe: 'RPE',
    mental_fatigue_slope: 'Mental Fatigue Trend',
    sleep_hours: 'Sleep Duration',
    sleep_score: 'Sleep Quality',
    rhr: 'Resting Heart Rate',
    hrv_rmssd: 'HRV (RMSSD)',
    hrv_7d_cv: 'HRV Variability',
    hrv_slope: 'HRV Trend',
    rhr_slope: 'RHR Trend',
    acwr_composite: 'ACWR',
    session_load_spike_ratio: 'Session Load Spike',
    load_change_pct_7d: 'Weekly Load Change',
    load_7d_sum: '7-Day Load',
    consecutive_high_soreness: 'Persistent Soreness',
    rpe_load_divergence: 'RPE-Load Mismatch',
    resp_rate: 'Respiratory Rate',
    skin_temp: 'Skin Temperature',
    load_volatility: 'Load Volatility',
    wearable_missing_rate_7d: 'Data Completeness',
    regional_pain_max: 'Regional Pain Max',
    regional_pain_3d_max: 'Regional Pain 3d Max',
    regional_pain_count: 'Regional Pain Count',
    regional_asymmetry: 'Regional Asymmetry',
  };
  return names[signal] || signal.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatRawValue(signal: string, value: number): string {
  // Subjective signals: 0 likely means no data was entered (scale is 1-10)
  if (['soreness', 'soreness_3d_max', 'rpe', 'mental_fatigue'].includes(signal)) {
    return value > 0 ? `${value.toFixed(0)}/10` : 'N/A';
  }
  if (signal === 'sleep_hours') return `${value.toFixed(1)} hrs`;
  if (signal === 'hrv_rmssd') return `${value > 0 ? value.toFixed(0) : '0'}ms`;
  if (signal === 'acwr_composite') return value.toFixed(2);
  if (signal === 'rhr') return `${value.toFixed(0)} bpm`;
  if (signal === 'acute_chronic_delta') return `${value > 0 ? '+' : ''}${value.toFixed(0)}`;
  if (signal === 'load_volatility') return value.toFixed(0);
  if (signal === 'resp_rate') return `${value.toFixed(1)} br/m`;
  if (signal === 'skin_temp') return `${celsiusToFahrenheit(value).toFixed(1)}°F`;
  if (signal.includes('pct') || signal.includes('change')) return `${value > 0 ? '+' : ''}${value.toFixed(0)}%`;
  if (signal.includes('spike') || signal.includes('ratio')) return `${(value * 100).toFixed(0)}%`;
  return value.toFixed(1);
}

export default InjuryRiskCard;
